/**
 * Setting or changing the account's password (api.md "Auth" `POST /api/auth/password`): the form the
 * sign-in screen shows after a code ("Set a password" / "Choose a new password") and the Account →
 * Profile dialog ("Set a password" / "Change password").
 *
 * A real <form> with the e-mail as a read-only `autocomplete="username"` field and the new password as
 * `autocomplete="new-password"` (the current one, when asked, as `current-password`), submitted with a
 * submit button — so browser password managers offer to save it. The rule is checked here first
 * (`passwordProblem`, the server's own rule) and again by the server.
 */

import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { PASSWORD_MIN_LENGTH, passwordProblem, toDigesterError, type SetPasswordResponse } from '@digester/core';
import { useQueryClient } from '@tanstack/react-query';
import { DialogFrame, isDialogRegistered, openDialog, registerDialog, type DialogProps } from '../app/dialogs.js';
import { showToast } from '../app/Toast.js';
import { useServices } from '../app/services.js';
import { setPassword } from '../platform/session.js';

/** "Saved" toast text, saying what happened to the other sessions. */
export function passwordSavedMessage(res: SetPasswordResponse, changed: boolean): string {
  const what = changed ? 'Password changed' : 'Password set';
  if (res.endedSessions === 0) return what;
  return `${what} · signed out on ${res.endedSessions} other browser${res.endedSessions === 1 ? '' : 's'} or app${res.endedSessions === 1 ? '' : 's'}`;
}

/** A server or network error as a sentence for this form. */
export function passwordErrorText(error: unknown): string {
  const e = toDigesterError(error);
  if (e.code === 'invalid_input' || e.code === 'rate_limited') return e.message;
  if (e.code === 'unauthenticated') return 'You were signed out. Sign in again, then set your password.';
  if (e.code === 'fetch_failed') return 'The server could not be reached. Check your connection and try again.';
  return 'The password could not be saved. Try again in a moment.';
}

export interface SetPasswordFormProps {
  email: string;
  /** Ask for the current password (the account has one and this is not a fresh code sign-in). */
  needCurrent: boolean;
  submitLabel: string;
  /** The secondary action: "Not now" on the sign-in screen, "Cancel" in the dialog. */
  skipLabel?: string;
  onSkip?(): void;
  onDone(res: SetPasswordResponse): void;
  /** Extra text above the actions. */
  note?: ReactNode;
  autoFocus?: boolean;
}

export function SetPasswordForm({ email, needCurrent, submitLabel, skipLabel, onSkip, onDone, note, autoFocus = true }: SetPasswordFormProps) {
  const { api } = useServices();
  const id = useId();
  const [current, setCurrent] = useState('');
  const [password, setPasswordValue] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const problem = passwordProblem(password, email);
    if (problem) {
      setError(problem);
      return;
    }
    if (needCurrent && current === '') {
      setError('Enter your current password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await setPassword(api, password, needCurrent ? current : undefined);
      setBusy(false);
      onDone(res);
    } catch (e) {
      setError(passwordErrorText(e));
      setBusy(false);
    }
  };

  return (
    <form className="password-form" noValidate onSubmit={(e) => void submit(e)} aria-describedby={`${id}-help`}>
      <label className="field">
        <span>Email</span>
        <input type="email" name="username" autoComplete="username" value={email} readOnly />
      </label>
      {needCurrent ? (
        <label className="field">
          <span>Current password</span>
          <input
            type={show ? 'text' : 'password'}
            name="current-password"
            autoComplete="current-password"
            required
            autoFocus={autoFocus}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
      ) : null}
      <label className="field">
        <span>{needCurrent ? 'New password' : 'Password'}</span>
        <input
          type={show ? 'text' : 'password'}
          name="new-password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          autoFocus={autoFocus && !needCurrent}
          value={password}
          onChange={(e) => setPasswordValue(e.target.value)}
        />
      </label>
      <p className="field-help" id={`${id}-help`}>
        At least {PASSWORD_MIN_LENGTH} characters — a few unrelated words work well. No other rules.
      </p>
      <label className="password-show">
        <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show password
      </label>
      {note ? <p className="field-help password-note">{note}</p> : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="account-actions">
        <button type="submit" className="primary-button" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onSkip ? (
          <button type="button" className="secondary-button" disabled={busy} onClick={onSkip}>
            {skipLabel ?? 'Not now'}
          </button>
        ) : null}
      </div>
    </form>
  );
}

/** Account → Profile → Set / Change password. */
export function PasswordDialog({ close, dialogId, email, hasPassword }: DialogProps & { email: string; hasPassword: boolean }) {
  const queryClient = useQueryClient();
  return (
    <DialogFrame title={hasPassword ? 'Change password' : 'Set a password'} className="small-dialog password-dialog" dialogId={dialogId} onClose={close}>
      <SetPasswordForm
        email={email}
        needCurrent={hasPassword}
        submitLabel={hasPassword ? 'Change password' : 'Set password'}
        skipLabel="Cancel"
        onSkip={close}
        note={
          <>
            Saving signs you out on your other browsers and apps; this one stays signed in and keeps syncing.
            {hasPassword ? ' Forgot your password? Sign out, then sign in with a code — you can choose a new one there.' : ''}
          </>
        }
        onDone={(res) => {
          queryClient.setQueryData(['account', 'me'], res.account);
          void queryClient.invalidateQueries({ queryKey: ['account'] });
          showToast(passwordSavedMessage(res, hasPassword));
          close();
        }}
      />
    </DialogFrame>
  );
}

export function openPasswordDialog(email: string, hasPassword: boolean): void {
  if (!isDialogRegistered('password')) registerDialog<{ email: string; hasPassword: boolean }>('password', PasswordDialog);
  openDialog('password', { email, hasPassword });
}
